import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore'
import { useSelector } from 'react-redux'

import { db } from '../firebase'

import { useProfileUser } from './useUser'

export const defaultSpeakableRubric = 'SuGg3NZlLYOTcLPrPlyc'

const useRubrics = (search = '') => {
  const uid = useSelector(({ user }) => user.auth.uid)
  const { recentRubrics = [], savedRubrics = [] } = useProfileUser()
  const { data = [], isPending } = useQuery({
    queryKey: ['rubrics'],
    queryFn: () => getRubrics(uid, recentRubrics, savedRubrics),
  })

  return {
    rubrics: data?.filter(
      rubric =>
        (rubric?.name && rubric?.name?.toLowerCase().includes(search.toLowerCase())) ||
        (rubric?.description && rubric?.description.toLowerCase().includes(search.toLowerCase())),
    ),
    isLoading: isPending,
  }
}

export default useRubrics

export const useRubric = (props = {}) => {
  const uid = useSelector(({ user }) => user.auth.uid)

  const { id = null, enabled = false } = props
  const queryClient = useQueryClient()
  const { data, isPending, isError } = useQuery({
    queryKey: ['rubric', id],
    queryFn: () => getRubric(id),
    enabled: enabled && !!id,
  })

  const createRubric = useMutation({
    mutationFn: async data => await createRubricFunction(data, uid),
    onSuccess: newRubric => {
      // queryClient.invalidateQueries(["rubric", id]);
      queryClient.invalidateQueries({ queryKey: ['rubric'] })
      queryClient.invalidateQueries({ queryKey: ['rubrics'] })
    },
  })

  const updateRubric = useMutation({
    mutationFn: updateRubricFunction,
    onSuccess: newRubric => {
      queryClient.invalidateQueries({ queryKey: ['rubric', id] })
      // cycle through the rubrics in queryClient and update the one that was updated
      const rubrics = queryClient.getQueryData(['rubrics'])

      if (!rubrics) return
      const updatedRubrics = rubrics.map(r => {
        if (r.id === id) {
          return newRubric
        }

        return r
      })

      queryClient.setQueryData(['rubrics'], updatedRubrics)
    },
  })

  const deleteRubric = useMutation({
    mutationFn: deleteRubricFunction,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rubrics'] })
    },
  })

  const copyRubric = useMutation({
    mutationFn: async id => await copyRubricFunction(id, uid),
    onSuccess: id => {
      queryClient.invalidateQueries({ queryKey: ['rubric', id] })
    },
  })

  return {
    rubric: data,
    isLoading: isPending,
    createRubric: createRubric.mutateAsync,
    updateRubric: updateRubric.mutateAsync,
    deleteRubric: deleteRubric.mutateAsync,
    copyRubric: copyRubric.mutateAsync,
  }
}

export const useRubricsArray = (ids = []) => {
  const { data, isPending } = useQuery({
    queryKey: ['recentRubrics', ids],
    queryFn: () => getRubricsArray(ids),
    enabled: !!ids.length,
  })

  return {
    rubrics: data,
    isLoading: isPending,
  }
}

const getRubricsArray = async ids => {
  try {
    const promises = ids.map(async id => {
      try {
        return await getRubric(id)
      } catch (error) {
        return null
      }
    })
    const rubrics = await Promise.all(promises)

    // const filtered = rubrics.filter((r) => r);
    return rubrics.filter(r => r)
  } catch (error) {
    console.error('Error fetching rubrics: ', error)
    throw new Error('Error fetching rubrics')
  }
}

const getRubrics = async (uid, recentRubrics = [], savedRubrics = []) => {
  try {
    const rubrics = []
    const querySnapshot = await getDocs(query(collection(db, 'rubrics'), where('owners', 'array-contains', uid)))
    const query2Snapshot = await getDocs(query(collection(db, 'rubrics'), where('template', '==', true)))

    querySnapshot.forEach(doc => {
      rubrics.push({ ...doc.data(), id: doc.id })
    })

    query2Snapshot.forEach(doc => {
      // only grab templates that arent yours
      rubrics.push({ ...doc.data(), id: doc.id })
    })
    const recentRubricsDocs = await getRubricsArray(recentRubrics)
    const savedRubricsDocs = await getRubricsArray(savedRubrics)

    // only add recent rubrics if theyre not in the existing rubrics
    recentRubricsDocs.forEach(doc => {
      if (rubrics && !rubrics.filter(r => r).find(r => r.id === doc.id)) {
        rubrics.push(doc)
      }
    })
    // only add saved rubrics if theyre not in the existing rubrics
    savedRubricsDocs.forEach(doc => {
      if (rubrics && !rubrics.filter(r => r).find(r => r.id === doc.id)) {
        rubrics.push(doc)
      }
    })
    // sort by lastModified (most recent first)
    const sortedRubrics = rubrics
      .filter(r => r)
      .sort((a, b) => {
        // if lastModified is not set, sort by createdAt
        if (!a.lastModified) {
          return b.createdAt - a.createdAt
        }

        return b.lastModified - a.lastModified
      })

    return sortedRubrics
  } catch (error) {
    console.error('Error fetching rubrics: ', error)
    throw new Error('Error fetching rubrics')
  }
}

const createRubricFunction = async (rubricData, uid) => {
  try {
    const rubricRef = await addDoc(collection(db, 'rubrics'), {
      ...rubricData,
      owners: [uid],
      createdAt: serverTimestamp(),
    })
    const newRubric = await getRubric(rubricRef.id)

    return newRubric
  } catch (error) {
    console.error('Error creating rubric: ', error)
    throw new Error('Error creating rubric')
  }
}

export const copyRubricFunction = async (rubricId, uid) => {
  try {
    const rubric = await getRubric(rubricId)
    const rubricRef = await addDoc(collection(db, 'rubrics'), {
      ...rubric,
      owners: [uid],
      createdAt: serverTimestamp(),
      lastModified: serverTimestamp(),
      name: `(Copy) ${rubric.name}`,
      template: false,
      copy: true,
      copiedFrom: rubricId,
    })

    // update the id property to the new id
    await updateRubricFunction({
      rubricId: rubricRef.id,
      updates: { id: rubricRef.id },
    })

    return rubricRef.id
  } catch (error) {
    console.error('Error copying rubric: ', error)
    throw new Error('Error copying rubric')
  }
}

const updateRubricFunction = async ({ rubricId, updates }) => {
  const rubricRef = doc(db, 'rubrics', rubricId)

  try {
    await updateDoc(rubricRef, { ...updates, lastModified: serverTimestamp() })

    const updatedRubric = await getRubric(rubricId)

    return updatedRubric
  } catch (error) {
    console.error('Error updating rubric: ', error)
    throw new Error('Error updating rubric')
  }
}

const deleteRubricFunction = async rubricId => {
  const rubricRef = doc(db, 'rubrics', rubricId)
  const batch = writeBatch(db)

  try {
    batch.delete(rubricRef)
    await batch.commit()
  } catch (error) {
    console.error('Error deleting rubric: ', error)
    throw new Error('Error deleting rubric')
  }
}

export const getRubric = async id => {
  try {
    const docRef = doc(db, 'rubrics', id)
    const docSnap = await getDoc(docRef)

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() }
    }

    // fallback to default rubric
    return await getRubric(defaultSpeakableRubric)
    // return null;
  } catch (error) {
    console.error('Error fetching rubric: ', error)
    throw new Error('Error fetching rubric')
  }
}
